import type { IconDecorationProtocol } from '@lighthouse/core'
import React, { useCallback, useRef } from 'react'
import styled from 'styled-components'

import { AudioStatusIcon } from './AudioStatusIcon'
import { formatBarDuration } from './format'

interface PlaybackBarProps {
    playing: boolean
    currentTime: number
    duration: number
    showDuration: boolean
    error: boolean
    disabled?: boolean
    icon?: IconDecorationProtocol
    iconColor?: string
    onGetFullUrl?: (url: string) => string
    onToggle: () => void
    onSeek: (time: number) => void
}

const Root = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 24px;
    min-width: 0;
    padding: 0 2px;
    color: var(--color-gray-800, #1d2939);
    user-select: none;
`

const IconButton = styled.button`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`

const Track = styled.div<{ disabled?: boolean }>`
    position: relative;
    flex: 1;
    min-width: 24px;
    height: 4px;
    border-radius: 999px;
    background: var(--color-gray-200, #e4e7ec);
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`

const Progress = styled.div`
    height: 100%;
    border-radius: inherit;
    background: var(--color-gray-800, #1d2939);
    pointer-events: none;
`

const Duration = styled.span`
    flex-shrink: 0;
    font-size: 12px;
    line-height: 16px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: var(--color-gray-500, #667085);
`

const getSeekTime = (event: React.PointerEvent<HTMLDivElement> | PointerEvent, duration: number) => {
    const target = event.currentTarget as HTMLDivElement
    const rect = target.getBoundingClientRect()
    const ratio = rect.width <= 0 ? 0 : Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
    return ratio * (Number.isFinite(duration) && duration > 0 ? duration : 0)
}

export const PlaybackBar: React.FC<PlaybackBarProps> = ({
    playing,
    currentTime,
    duration,
    showDuration,
    error,
    disabled,
    icon,
    iconColor,
    onGetFullUrl,
    onToggle,
    onSeek
}) => {
    const draggingRef = useRef(false)
    const unavailable = disabled || error
    const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0

    const handleSeekFromEvent = useCallback(
        (event: React.PointerEvent<HTMLDivElement> | PointerEvent) => {
            if (unavailable) {
                return
            }
            onSeek(getSeekTime(event, duration))
        },
        [duration, onSeek, unavailable]
    )

    const handlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (unavailable) {
                return
            }
            event.preventDefault()
            event.stopPropagation()
            draggingRef.current = true
            event.currentTarget.setPointerCapture(event.pointerId)
            handleSeekFromEvent(event)
        },
        [handleSeekFromEvent, unavailable]
    )

    const handlePointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!draggingRef.current) {
                return
            }
            handleSeekFromEvent(event)
        },
        [handleSeekFromEvent]
    )

    const stopDragging = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current) {
            return
        }
        draggingRef.current = false
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
        }
    }, [])

    return (
        <Root>
            <IconButton type="button" disabled={unavailable} onClick={onToggle} data-stop-action-propagation>
                <AudioStatusIcon playing={playing} icon={icon} color={iconColor} onGetFullUrl={onGetFullUrl} />
            </IconButton>
            <Track
                disabled={unavailable}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDragging}
                onPointerCancel={stopDragging}
                data-stop-action-propagation
            >
                <Progress style={{ width: `${progress}%` }} />
            </Track>
            {showDuration && <Duration>{formatBarDuration(currentTime, duration, error)}</Duration>}
        </Root>
    )
}
